import {
  Check,
  Database,
  Flag,
  Lock,
  RefreshCw,
  RotateCcw,
  Undo2,
  XCircle,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  DataTable,
  type DataTableColumn,
} from "@/components/cinch/DataTable/DataTable";
import { KpiTile } from "@/components/cinch/KpiTile";
import { IconButton } from "@/components/cinch/IconButton";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import {
  monthLabel,
  useGap,
  usePullGap,
  useReviewGapContract,
  useSetDebooked,
  type GapContract,
  type GapDisposition,
  type ReviewStatus,
} from "../api";
import { fmtCur, fmtCur2, fmtDate, relativeAgo } from "../../renewals/format";

const DISPOSITION_LABEL: Record<GapDisposition, string> = {
  not_started_yet: "Not started yet",
  should_have_billed: "Should have billed",
  debook_candidate: "Debook candidate",
};

/** R-03 worklist: contracts that are booked but have never billed a dollar.
 *
 *  Each row is classified so the reviewer knows the next move — a contract
 *  whose billing starts next month is fine; one that's months overdue or whose
 *  onboarding was abandoned is the debook conversation. Debooking is stored in
 *  cinch_ops (Cinch's billing mart has no debooked column) and is DURABLE, so
 *  a debooked contract stays off future months' lists. */
export function GapAnalysisPanel({ period }: { period: string }) {
  const { data, isLoading, error } = useGap(period);
  const pull = usePullGap(period);
  const review = useReviewGapContract(period);
  const setDebooked = useSetDebooked(period);
  const [tab, setTab] = useState<GapDisposition | "all" | "debooked">("all");
  const [debookTarget, setDebookTarget] = useState<GapContract | null>(null);
  const [flagTarget, setFlagTarget] = useState<GapContract | null>(null);

  const contracts = useMemo(() => data?.contracts ?? [], [data]);
  const debooked = useMemo(() => data?.debooked ?? [], [data]);
  const rows = useMemo(
    () =>
      tab === "all" || tab === "debooked"
        ? contracts
        : contracts.filter((c) => c.disposition === tab),
    [contracts, tab],
  );

  const canAct = (data?.can_act ?? false) && !(data?.locked ?? false);
  const pulled = !!data?.pulled_at;
  const s = data?.summary;

  function doPull() {
    pull.mutate(undefined, {
      onSuccess: (p) =>
        toast.success(
          `Pulled the gap — ${p.summary?.contract_count ?? 0} never-billed contracts (${fmtCur(p.summary?.gap_mrr ?? 0)} MRR)`,
        ),
      onError: (e) => toast.error("Pull failed", { description: (e as Error).message }),
    });
  }

  if (isLoading && !data) return <Card className="h-64 animate-pulse" />;

  if (!isLoading && !pulled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Step data</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center gap-3 rounded-md border border-dashed bg-muted/30 px-4 py-10 text-center">
            <Database className="h-8 w-8 text-muted-foreground" />
            <p className="max-w-md text-sm text-muted-foreground">
              The booked-vs-billed gap hasn't been pulled for {monthLabel(period)}.
              Pulling lists every contract that's on the books but has never
              billed a dollar, classified by whether that's expected yet.
            </p>
            {canAct ? (
              <Button onClick={doPull} disabled={pull.isPending}>
                <RefreshCw
                  className={cn("mr-1.5 h-4 w-4", pull.isPending && "animate-spin")}
                />
                {pull.isPending ? "Pulling…" : "Pull from Snowflake"}
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">
                An R-03 owner (CRO / Finance) runs the pull.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  const columns: DataTableColumn<GapContract>[] = [
    {
      key: "company",
      header: "Company",
      className: "max-w-[240px]",
      render: (c) => (
        <div className="min-w-0">
          <span className="block truncate text-sm font-semibold">
            {c.company_name ?? c.cinch_account_name ?? "—"}
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            {c.company_dba ? `dba ${c.company_dba} · ` : ""}
            {c.contract_type_name ?? "—"}
          </span>
        </div>
      ),
    },
    {
      key: "disposition",
      header: "Why",
      render: (c) => (
        <div className="flex flex-col items-start gap-0.5">
          <Badge
            variant={
              c.disposition === "debook_candidate"
                ? "danger"
                : c.disposition === "should_have_billed"
                  ? "warning"
                  : "neutral"
            }
          >
            {DISPOSITION_LABEL[c.disposition]}
          </Badge>
          {c.is_onboard_abandoned && (
            <span className="text-[11px] text-danger">onboarding abandoned</span>
          )}
        </div>
      ),
    },
    {
      key: "months_overdue",
      header: "Overdue",
      align: "center",
      render: (c) =>
        c.disposition === "not_started_yet" ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          <span
            className={cn(
              "tabular-nums text-sm",
              c.months_overdue >= 3 && "font-semibold text-danger",
            )}
            title="Months from when billing should have started to the close month end"
          >
            {c.months_overdue} mo
          </span>
        ),
    },
    {
      key: "contract_signed_at",
      header: "Signed",
      render: (c) => fmtDate(c.contract_signed_at),
    },
    {
      key: "billing_effective_from",
      header: "Billing Effective",
      render: (c) =>
        c.billing_effective_from ? (
          fmtDate(c.billing_effective_from)
        ) : (
          <span className="font-medium text-warning">missing</span>
        ),
    },
    {
      key: "mrr_total",
      header: "Gap MRR",
      numeric: true,
      render: (c) => <span className="font-semibold">{fmtCur2(c.mrr_total)}</span>,
    },
    {
      key: "closer_name",
      header: "Closer",
      render: (c) => c.closer_name ?? "—",
    },
    {
      key: "review",
      header: "Review",
      align: "right",
      className: "w-52",
      render: (c) => (
        <span className="inline-flex items-center justify-end gap-1.5">
          <StatusBadge value={c.review_status} />
          {canAct && (
            <>
              <IconButton
                title="Approve — expected, no action needed"
                active={c.review_status === "approved"}
                activeClassName="text-success"
                hoverClassName="hover:bg-success-light hover:text-success"
                onClick={() =>
                  review.mutate(
                    { contractId: c.contract_id, review: { status: "approved" } },
                    {
                      onError: (e) =>
                        toast.error("Couldn't approve", {
                          description: (e as Error).message,
                        }),
                    },
                  )
                }
              >
                <Check className="h-3.5 w-3.5" />
              </IconButton>
              <IconButton
                title="Flag — needs chasing (add notes)"
                active={c.review_status === "flagged"}
                activeClassName="text-warning"
                hoverClassName="hover:bg-warning-light hover:text-warning"
                onClick={() => setFlagTarget(c)}
              >
                <Flag className="h-3.5 w-3.5" />
              </IconButton>
              <IconButton
                title="Debook — write this contract off the books"
                active={false}
                activeClassName=""
                hoverClassName="hover:bg-danger/10 hover:text-danger"
                onClick={() => setDebookTarget(c)}
              >
                <XCircle className="h-3.5 w-3.5" />
              </IconButton>
            </>
          )}
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}
      >
        <KpiTile
          label="Booked, never billed"
          value={String(s?.contract_count ?? 0)}
          sub={`${s?.not_started_count ?? 0} not due yet`}
        />
        <KpiTile
          label="Gap"
          value={fmtCur2(s?.gap_mrr ?? 0)}
          sub={`${fmtCur(s?.gap_arr ?? 0)} annualized`}
          tone={(s?.gap_mrr ?? 0) > 0 ? "warning" : "positive"}
        />
        <KpiTile
          label="Debook candidates"
          value={String(s?.debook_candidate_count ?? 0)}
          tone={(s?.debook_candidate_count ?? 0) > 0 ? "danger" : "positive"}
          sub={`${fmtCur(s?.debook_candidate_mrr ?? 0)} MRR at stake`}
          onClick={() =>
            setTab(tab === "debook_candidate" ? "all" : "debook_candidate")
          }
          selected={tab === "debook_candidate"}
        />
        <KpiTile
          label="Flags"
          value={String(s?.flagged_count ?? 0)}
          tone={(s?.flagged_count ?? 0) > 0 ? "danger" : "positive"}
          sub={
            (s?.flagged_count ?? 0) > 0
              ? "must be cleared before R-03 completes"
              : `${s?.approved_count ?? 0} approved · ${s?.pending_count ?? 0} pending`
          }
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1 border-b border-border">
          {(
            [
              ["all", `All (${contracts.length})`],
              ["debook_candidate", `Debook candidates (${s?.debook_candidate_count ?? 0})`],
              ["should_have_billed", `Should have billed (${s?.should_have_billed_count ?? 0})`],
              ["not_started_yet", `Not due yet (${s?.not_started_count ?? 0})`],
              ["debooked", `Debooked (${debooked.length})`],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={cn(
                "rounded-t-md border-b-2 px-3 py-1.5 text-sm font-medium",
                tab === key
                  ? "border-brand text-brand"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {data?.locked && (
            <span className="inline-flex items-center gap-1 font-medium text-success">
              <Lock className="h-3.5 w-3.5" /> Signed off — frozen
            </span>
          )}
          {data?.pulled_at && (
            <span title={new Date(data.pulled_at).toLocaleString()}>
              Pulled {relativeAgo(data.pulled_at)}
              {data.pulled_by_name ? ` by ${data.pulled_by_name}` : ""}
            </span>
          )}
          {canAct && (
            <Button variant="outline" size="sm" onClick={doPull} disabled={pull.isPending}>
              <RefreshCw
                className={cn("mr-1.5 h-3.5 w-3.5", pull.isPending && "animate-spin")}
              />
              {pull.isPending ? "Pulling…" : "Re-pull"}
            </Button>
          )}
        </div>
      </div>

      {tab === "debooked" ? (
        <DebookedList
          rows={debooked}
          canAct={canAct}
          onUndo={(contractId, company) =>
            setDebooked.mutate(
              { contractId },
              {
                onSuccess: () =>
                  toast.success(`${company ?? contractId} is back on the books`),
                onError: (e) =>
                  toast.error("Couldn't reverse the debook", {
                    description: (e as Error).message,
                  }),
              },
            )
          }
        />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(c) => c.contract_id}
          error={error ? String(error) : null}
          emptyMessage="Nothing here — every booked contract is billing."
          containerClassName="max-h-[calc(100vh-18rem)]"
        />
      )}

      <p className="text-xs text-muted-foreground">
        Billing is tracked per account, so "never billed" means the contract's
        account has never billed a dollar — exact for a first contract,
        conservative for an upsell on an already-billing account. Purchase
        orders are excluded: bundled POs bill under their reseller by design.
        Debooking is recorded here in cinch_ops (Cinch's billing tables have no
        debooked field) and carries forward to every future close.
      </p>

      <DebookDialog
        contract={debookTarget}
        pending={setDebooked.isPending}
        onClose={() => setDebookTarget(null)}
        onConfirm={(reason) => {
          const c = debookTarget;
          if (!c) return;
          setDebooked.mutate(
            { contractId: c.contract_id, reason },
            {
              onSuccess: () => {
                toast.success(`${c.company_name ?? c.contract_id} debooked`);
                setDebookTarget(null);
              },
              onError: (e) =>
                toast.error("Couldn't debook", { description: (e as Error).message }),
            },
          );
        }}
      />

      <FlagDialog
        period={period}
        contract={flagTarget}
        onClose={() => setFlagTarget(null)}
      />
    </div>
  );
}

// ── the standing debook list ────────────────────────────────────────────────

function DebookedList({
  rows,
  canAct,
  onUndo,
}: {
  rows: import("../api").DebookedContract[];
  canAct: boolean;
  onUndo: (contractId: string, company: string | null) => void;
}) {
  if (rows.length === 0) {
    return (
      <p className="rounded-md border border-dashed bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
        Nothing has been debooked yet.
      </p>
    );
  }
  return (
    <Card className="divide-y divide-border overflow-hidden p-0">
      {rows.map((d) => (
        <div key={d.contract_id} className="flex flex-wrap items-start gap-3 px-4 py-3">
          <div className="min-w-0 flex-1 basis-64">
            <div className="truncate text-sm font-semibold">
              {d.company_name ?? d.contract_id}
            </div>
            <div className="text-xs text-muted-foreground">
              Debooked by {d.decided_by_name ?? "—"}
              {d.decided_at ? ` on ${new Date(d.decided_at).toLocaleString()}` : ""}
              {d.decided_in_period ? ` · ${monthLabel(d.decided_in_period)} close` : ""}
            </div>
            {d.reason && <div className="mt-1 text-xs">“{d.reason}”</div>}
          </div>
          {canAct && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onUndo(d.contract_id, d.company_name)}
            >
              <Undo2 className="mr-1.5 h-3.5 w-3.5" /> Put back on the books
            </Button>
          )}
        </div>
      ))}
    </Card>
  );
}

// ── dialogs ─────────────────────────────────────────────────────────────────

function DebookDialog({
  contract,
  pending,
  onClose,
  onConfirm,
}: {
  contract: GapContract | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const seen = useState<string | null>(null);
  if (contract && seen[0] !== contract.contract_id) {
    seen[1](contract.contract_id);
    setReason("");
  }
  return (
    <Dialog open={contract != null} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Debook {contract?.company_name ?? ""}?</DialogTitle>
          <DialogDescription>
            This writes {fmtCur2(contract?.mrr_total ?? 0)} MRR off the books and
            removes the contract from this and every future month's gap list
            until someone puts it back. The reason is required and is kept with
            your name and the date.
          </DialogDescription>
        </DialogHeader>
        {contract && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border px-3 py-2 text-xs">
            <div>
              <dt className="text-muted-foreground">Signed</dt>
              <dd>{fmtDate(contract.contract_signed_at)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Billing effective</dt>
              <dd>{contract.billing_effective_from ? fmtDate(contract.billing_effective_from) : "missing"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Overdue</dt>
              <dd>{contract.months_overdue} months</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Onboarding</dt>
              <dd>
                {contract.is_onboard_abandoned
                  ? "abandoned"
                  : contract.is_onboard_complete
                    ? "complete"
                    : "in progress"}
              </dd>
            </div>
          </dl>
        )}
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          aria-label="Debook reason"
          placeholder="Why is this being debooked? (e.g. onboarding abandoned in 2022, never billed)"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(reason.trim())}
            disabled={reason.trim().length < 3 || pending}
          >
            <XCircle className="mr-1.5 h-4 w-4" />
            {pending ? "Debooking…" : "Debook contract"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FlagDialog({
  period,
  contract,
  onClose,
}: {
  period: string;
  contract: GapContract | null;
  onClose: () => void;
}) {
  const review = useReviewGapContract(period);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState<ReviewStatus>("flagged");
  const seen = useState<string | null>(null);
  if (contract && seen[0] !== contract.contract_id) {
    seen[1](contract.contract_id);
    setNotes(contract.review_notes ?? "");
    setStatus("flagged");
  }
  const name = contract?.company_name ?? contract?.cinch_account_name ?? "";

  return (
    <Dialog open={contract != null} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Review {name}</DialogTitle>
          <DialogDescription>
            Flagged rows block completing this step until they're approved or
            cleared. Note what needs chasing.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {(
            [
              ["flagged", Flag, "Flag"],
              ["approved", Check, "Approve"],
              ["pending", RotateCcw, "Clear"],
            ] as const
          ).map(([value, Icon, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setStatus(value)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium",
                status === value
                  ? value === "flagged"
                    ? "border-warning bg-warning-light text-warning"
                    : value === "approved"
                      ? "border-success bg-success-light text-success"
                      : "border-border bg-muted text-foreground"
                  : "border-border text-muted-foreground hover:bg-muted",
              )}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          aria-label="Review notes"
          placeholder="What needs to happen with this contract?"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!contract) return;
              review.mutate(
                { contractId: contract.contract_id, review: { status, notes } },
                {
                  onSuccess: () => {
                    toast.success(`${name} ${status === "pending" ? "cleared" : status}`);
                    onClose();
                  },
                  onError: (e) =>
                    toast.error("Couldn't save review", {
                      description: (e as Error).message,
                    }),
                },
              );
            }}
            disabled={review.isPending}
          >
            {review.isPending ? "Saving…" : "Save review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
